import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { NgModule, InjectionToken, Injectable, Optional, Inject } from '@angular/core';
import { guid } from '@firestitch/common';
import { format } from 'date-fns';

class FsTransferModule {
    static forRoot() {
        return {
            ngModule: FsTransferModule,
        };
    }
}
FsTransferModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsTransferModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsTransferModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsTransferModule, imports: [CommonModule] });
FsTransferModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsTransferModule, imports: [[
            CommonModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsTransferModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                }]
        }] });

const FS_TRANSFER_HANDLER = new InjectionToken('fs-transfer.request_interceptors');

class FsTransferHandler {
    begin(request) {
        return request;
    }
    error(data, raw) { }
}

class Request {
    constructor(method, path, params) {
        this.params = {};
        this.method = method;
        this.path = path;
        this.params = params;
    }
    clone(data) {
        return Object.assign({}, this, data);
    }
}

class FsTransferService {
    constructor(
    // Custom interceptors
    handler) {
        this.handler = handler;
    }
    post(path, parameters = {}) {
        return this.request(path, 'post', parameters);
    }
    request(path, method = 'get', parameters = {}) {
        this._cleanup();
        const request = this.handler.begin(new Request(method, path, parameters));
        const uuid = guid();
        const container = this.initContainer();
        const iframe = this.initFrame(uuid);
        const form = this.initForm(request, uuid);
        const div = document.createElement('div');
        div.setAttribute('id', `fs-transfer-${uuid}`);
        div.setAttribute('data-time', (new Date()).getTime().toString());
        div.classList.add('fs-transfer');
        div.appendChild(form);
        div.appendChild(iframe);
        div.setAttribute('style', 'display: none;');
        container.appendChild(div);
        form.submit();
        iframe.setAttribute('submitted', 'true');
    }
    initContainer() {
        let container = document.getElementById('fs-transfer-container');
        if (!container) {
            container = document.createElement('div');
            container.setAttribute('id', 'fs-transfer-container');
            document.body.appendChild(container);
        }
        return container;
    }
    initFrame(uniqID) {
        const iframe = document.createElement('iframe');
        iframe.setAttribute('src', 'about:blank');
        iframe.setAttribute('name', `fs-transfer-iframe-${uniqID}`);
        iframe.setAttribute('id', `fs-transfer-iframe-${uniqID}`);
        iframe.onload = (event) => {
            if (!event.target.hasAttribute('submitted')) {
                return;
            }
            let data = {};
            let raw = '';
            try {
                // Needs to be the same domain to see the body content
                const iframeBody = iframe.contentWindow.document.body;
                raw = iframeBody.innerText;
                data = JSON.parse(raw);
            }
            catch (e) { }
            this.handler.error(data, raw);
        };
        return iframe;
    }
    initForm(request, uniqID) {
        const form = document.createElement('form');
        form.setAttribute('action', request.path);
        form.setAttribute('method', request.method);
        form.setAttribute('target', `fs-transfer-iframe-${uniqID}`);
        this._objectToForm(request.params, form);
        return form;
    }
    _cleanup() {
        // Expiry 1 day
        const expiryTime = (new Date()).getTime() - (60 * 60 * 24);
        document.querySelectorAll('#fs-transfer-container .fs-transfer')
            .forEach((el) => {
            if (parseInt(el.getAttribute('data-time')) < expiryTime) {
                el.remove();
            }
        });
    }
    _objectToForm(target, form, namespace = null, level = 0) {
        level++;
        // Depth limit
        if (level > 10) {
            throw Error('Maximum call stack size exceeded');
        }
        if (target === void 0 || target === null) {
            return;
        }
        Object.keys(target).forEach((property) => {
            let item = target[property];
            const formKey = namespace ? `${namespace}[${property}]` : property;
            if (item instanceof Date) {
                item = format(item, `yyyy-MM-dd'T'HH:mm:ssxxx`);
                this._appendForm(form, formKey, item);
            }
            else if (item && typeof item === 'object') {
                this._objectToForm(item, form, formKey, level);
            }
            else if (item !== undefined) {
                this._appendForm(form, formKey, item);
            }
        });
    }
    _appendForm(form, name, value) {
        const paramInput = document.createElement('input');
        paramInput.setAttribute('type', 'hidden');
        paramInput.setAttribute('name', name);
        paramInput.setAttribute('value', value);
        form.appendChild(paramInput);
    }
}
FsTransferService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsTransferService, deps: [{ token: FS_TRANSFER_HANDLER, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
FsTransferService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsTransferService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsTransferService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: FsTransferHandler, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [FS_TRANSFER_HANDLER]
                }] }]; } });

class RequestHandler {
    constructor(next, interceptor) {
        this.next = next;
        this.interceptor = interceptor;
    }
    handle(req) {
        return this.interceptor.intercept(req, this.next);
    }
}

/*
 * Public API
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FS_TRANSFER_HANDLER, FsTransferHandler, FsTransferModule, FsTransferService, Request, RequestHandler };
